import type {
    ApiCommerceOffer,
    ApiSmStateSnapshot,
} from "@bridge-applications/pb-types";
import { energyAmountForOffer, telegramStarsPrice } from "./commerce.ts";

export interface KeepPlayingRefill {
    secondsRemaining: number;
    spins: string;
}

/** Display only the next refill promised by the authoritative slot snapshot. */
export function keepPlayingRefill(
    snapshot: ApiSmStateSnapshot | null,
    now: number,
): KeepPlayingRefill | null {
    const nextRefillAt = snapshot?.player.nextRefillAt;
    if (!snapshot || !nextRefillAt) return null;
    const { player, campaign } = snapshot;
    const energy = BigInt(player.energy);
    const cap = BigInt(campaign.energyRefillCap);
    const room = cap > energy ? cap - energy : 0n;
    const refill = BigInt(campaign.energyRefillAmount);
    const nextEnergy = energy + (refill < room ? refill : room);
    const spins = nextEnergy / BigInt(campaign.betEnergy);
    if (spins === 0n || room === 0n) return null;
    return {
        secondsRemaining: Math.max(
            0,
            Math.ceil((Date.parse(nextRefillAt) - now) / 1_000),
        ),
        spins: spins.toString(),
    };
}

/** The footer names minutes, so even long waits use total minutes:seconds. */
export function keepPlayingCountdown(seconds: number): string {
    const remaining = Math.max(0, Math.ceil(seconds));
    return `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
}

export function keepPlayingOfferAvailability(
    offer: ApiCommerceOffer,
    now: number,
): "available" | "expired" | "unavailable" {
    if (offer.endsAt && Date.parse(offer.endsAt) <= now) return "expired";
    const energy = energyAmountForOffer(offer);
    if (
        !energy ||
        BigInt(energy) <= 0n ||
        !telegramStarsPrice(offer) ||
        (offer.startsAt && Date.parse(offer.startsAt) > now) ||
        (offer.purchaseLimitPerUser !== null &&
            offer.fulfilledPurchaseCount >= offer.purchaseLimitPerUser)
    ) {
        return "unavailable";
    }
    return "available";
}
